import { AlertTriangle, Check } from '@timelyapp/icons/solid-icons';
import { Box, LoadingIcon, Text } from '@timelyapp/react';
import RichTooltip from 'components/RichTooltip';
import { appIcons } from 'constants/known-apps.icons';
import { startCase } from 'lodash-es';
import React, { useMemo } from 'react';
import type { HourSyncStatus as HourSyncStatusType } from 'types/syncStatus';
import styles from './HourSyncStatus.module.scss';

interface Props {
  status: Array<HourSyncStatusType>;
}

export const HourSyncStatus: React.FC<Props> = ({ status: statusList }) => {
  const status = statusList[0];

  const icon = useMemo(() => {
    switch (status.status) {
      case 'pending':
        return (
          <div className={styles.pendingIcon}>
            <LoadingIcon />
          </div>
        );
      case 'synced':
        return status.external_url ? (
          <a
            href={status.external_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className={styles.syncedLink}
          >
            <Check className={styles.syncedIcon} />
          </a>
        ) : (
          <div className={styles.syncedLink}>
            <Check className={styles.syncedIcon} />
          </div>
        );
      case 'failed':
      case 'auth_required':
        return <SyncFailedIcon />;

      default:
        return null;
    }
  }, [status]);

  if (!status) {
    return null;
  }

  return (
    <RichTooltip
      position="top"
      tooltip={() => <HourSyncStatusTooltip status={status} />}
      childrenClassName={styles.tooltipWrap}
      className={styles.tooltip}
    >
      {icon}
    </RichTooltip>
  );
};

const SyncFailedIcon: React.FC = () => (
  <div className={styles.failedIcon}>
    <AlertTriangle />
  </div>
);

const HourSyncStatusTooltip: React.FC<{ status: HourSyncStatusType }> = ({ status }) => {
  const syncedAtFormatted = status.synced_at ? formatSmartDate(new Date(status.synced_at)) : null;
  const updatedAtFormatted = status.updated_at
    ? formatSmartDate(new Date(status.updated_at))
    : null;

  switch (status.status) {
    case 'pending': {
      return (
        <Text.Paragraph className={styles.oneLine}>
          Syncing to <ToolIdentifier tool={status.tool_id} /> since {updatedAtFormatted}...
        </Text.Paragraph>
      );
    }

    case 'synced': {
      return (
        <Text.Paragraph className={styles.oneLine}>
          Synced to <ToolIdentifier tool={status.tool_id} /> at{' '}
          {syncedAtFormatted ?? updatedAtFormatted}
        </Text.Paragraph>
      );
    }

    case 'failed': {
      return (
        <Box.Column gap="large">
          <Text.Label size="large" className={styles.oneLine}>
            Sync to <ToolIdentifier tool={status.tool_id} /> failed
          </Text.Label>
          <Text.Paragraph size="small" style={{ opacity: 0.8 }}>
            Last attempt at {updatedAtFormatted}
          </Text.Paragraph>
          {status.error_message && (
            <Text.Paragraph size="small" style={{ whiteSpace: 'pre-wrap' }}>
              {status.error_message}
            </Text.Paragraph>
          )}
        </Box.Column>
      );
    }
    case 'auth_required': {
      return (
        <Box.Column gap="large">
          <Text.Label size="large" className={styles.oneLine}>
            Re-authentication required
          </Text.Label>
          <Text.Paragraph size="small" style={{ opacity: 0.8 }}>
            Last attempt at {updatedAtFormatted}
          </Text.Paragraph>
          <Text.Paragraph size="small">
            The user needs to re-authenticate their personal connection to{' '}
            <ToolIdentifier tool={status.tool_id} /> to allow syncing this time entry.
          </Text.Paragraph>
        </Box.Column>
      );
    }

    default:
      return null;
  }
};

const ToolIdentifier: React.FC<{ tool: string }> = ({ tool }) => {
  const toolTitle = startCase(tool.replace('_', ' '));
  const icon = appIcons[tool];
  const iconElement = icon ? <img src={icon} alt={toolTitle} className={styles.toolIcon} /> : null;
  return (
    <span className={styles.toolIdentifier}>
      {iconElement}
      {toolTitle}
    </span>
  );
};

function formatSmartDate(date: Date, now = new Date()) {
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
